import {
  WorksheetAssociatedTestSection,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
} from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { Field, NumericInput, TextOutput } from '../../../../../Core';
import { NamePrefixModifier, withInjectedProps } from '../../../../../Core/InjectedPropsContext';
import { ListFieldSection } from '../../../../../Layout';

let InitialSeparationWetMaterialSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const isWashed = useSelector(
    pipe(createWorksheetSelector!('Separation.FinePortion.Subspecimen.Washing.IsWashed'), valueOrDefault(false))
  );

  return (
    <>
      <ListFieldSection>
        <Field input={<TextOutput />} name="Masses.WetMass" label="Specimen Total Wet Mass" />
        <NamePrefixModifier appendNamePrefix="Separation">
          {/* Just one balance input because the same balance is used for:
              - Fine portion wet mass
              - Coarse portion dry mass
              - Fine portion subspecimen wet mass (when it will be washed, to save drying it first)
          */}
          <WorksheetBalanceFieldSet
            name="CoarsePortion.Masses.MassMeasurement.Equipment.Balance"
            copyTo={[
              'CoarsePortion.Masses.MassMeasurement.Equipment.DryMassBalance',
              'FinePortion.Masses.MassMeasurement.Equipment.Balance',
              'FinePortion.Subspecimen.Masses.MassMeasurement.Equipment.Balance',
            ]}
          />
          <Field
            input={<WorksheetEquipmentDropdown />}
            name="CoarsePortion.Masses.MassMeasurement.Equipment.Oven"
            equipmentType="oven"
            copyTo={[
              'FinePortion.Masses.MassMeasurement.Equipment.Oven',
              'FinePortion.Subspecimen.Masses.MassMeasurement.Equipment.Oven',
            ]}
          />
        </NamePrefixModifier>

        {/* We use modified labels / message paths for the Coarse Portion and Fine Portion masses
            because tare mass is not used */}
        <NamePrefixModifier appendNamePrefix="Separation.FinePortion.Masses">
          <Field
            input={<NumericInput />}
            label="Fine Portion Wet Mass"
            name="MassMeasurement.TareAndWetMass"
            additionalMessagePaths={['MassMeasurement.WetMass', 'WetMass']}
          />
        </NamePrefixModifier>
        <NamePrefixModifier appendNamePrefix="Separation.CoarsePortion.Masses">
          <Field
            input={<NumericInput />}
            label="Coarse Portion Dry Mass"
            name="MassMeasurement.TareAndDryMass"
            additionalMessagePaths={['MassMeasurement.DryMass', 'DryMass']}
          />
        </NamePrefixModifier>
      </ListFieldSection>

      <WorksheetAssociatedTestSection
        name="Separation.FinePortion.Subspecimen.Masses.AssociatedMoistureContent"
        heading="Fine Portion Water Content"
        testType="Moisture Content"
        associatedTestModel={{
          measured: true,
        }}
      />
      <ListFieldSection>
        <Field input={<TextOutput />} name="Separation.FinePortion.Masses.DryMass" />
        <Field input={<TextOutput />} name="Masses.DryMass" label="Specimen Total Dry Mass" />
      </ListFieldSection>

      <NamePrefixModifier appendNamePrefix="Separation.FinePortion.Subspecimen.Masses">
        {/*
         If the fine portion subspecimen is being washed after separation, the wet mass is measured
         (using the same balance as above), and the dry mass is calculated.
         If the fine portion subspecimen is NOT being washed after separation, it needs to be dried
         first (all sieving is done on dry material for this method), and then the dry mass is
         measured, possibly with a different balance.
         */}
        {isWashed ? (
          <ListFieldSection heading="Fine Portion Subspecimen Mass">
            <NamePrefixModifier appendNamePrefix="MassMeasurement">
              <Field input={<NumericInput />} name="TareMass" />
              <Field input={<NumericInput />} name="TareAndWetMass" />
            </NamePrefixModifier>
            <Field input={<TextOutput />} name="WetMass" />
            <Field input={<TextOutput />} name="DryMass" />
          </ListFieldSection>
        ) : (
          <ListFieldSection heading="Fine Portion Subspecimen Mass">
            <NamePrefixModifier appendNamePrefix="MassMeasurement">
              <WorksheetBalanceFieldSet name="Equipment.DryMassBalance" />
              <Field input={<NumericInput />} name="TareMass" />
              <Field input={<NumericInput />} name="TareAndDryMass" />
            </NamePrefixModifier>
            <Field input={<TextOutput />} name="DryMass" />
          </ListFieldSection>
        )}
      </NamePrefixModifier>
    </>
  );
};

InitialSeparationWetMaterialSection = withInjectedProps(InitialSeparationWetMaterialSection);

export { InitialSeparationWetMaterialSection };
export default InitialSeparationWetMaterialSection;
